import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import TextEditable from 'components/text-editable/text-editable';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PrioritySelector from 'components/priority-select/priority-select';
import { connect, useSelector } from 'react-redux';
import { ProductionStatusEnum } from 'types/status-enums';
import { AccessLevel, Permission } from 'services/permission.model';
import { PermissionGuardSelectors } from 'modules/permission-guard/permission-guard.controller';
import MaxLettersWarning from 'components/max-letters-warning/max-letters-warning';
import {
  Actions as ProductionWorkflowAdditionalTasksActions,
  ProductionWorkflowTaskTWithIssues,
} from 'pages/production-workflow/controllers/production-workflow-additional-tasks.controller';
import { Actions as ManageFailedTasksActions } from 'modules/manage-failed-tasks-modal/manage-failed-tasks.controller';
import { ProductionWorkflowTaskT } from 'services/production-workflow.model';
import ActionsMenu from 'pages/production-workflow/modules/additional-task-item/components/action-menu/actions-menu';
import StatusSelector from 'components/status-selector/status-selector';
import { Paths } from 'routes/paths';
import MultipleAssigneeSelector, {
  MultipleAssigneeSelectorSlotsT,
} from 'components/ui-new/multiple-assignee-selector/multiple-assignee-selector';
import { AppState } from 'redux/store';
import { UserShortModel } from 'services/user.model';
import { LightTooltip } from 'components/ui-new/light-tooltip/light-tooltip';
import Tag from 'components/tag-outline/tag-outline';
import { Stack } from '@mui/material';
import IssuesList from 'pages/production/components/production-item/issues-list/issues-list';
import { WarningIcon } from 'icons/warning';
import ProcessingIndicator from 'components/ui-new/processing-indicator/processing-indicator';
import s from './additional-task-item.module.scss';

type OwnProps = {
  highlightName?: string;
  isProcessingGuard: boolean;
  isShowDashedLine?: boolean;
  isItemForQualityControl?: boolean;
  taskInfo: ProductionWorkflowTaskTWithIssues;
};
type StateProps = {
  productionStatus: ProductionStatusEnum;
};
type DispatchProps = {
  closeManageFailedTasksModal: () => void;
  copyAdditionalTaskLink: (taskId: string) => void;
  deleteTemporaryAdditionalTask: (id: string) => void;
  openDeleteConfirmationModal: (taskId: string) => void;
  createAdditionalTask: (id: string, value: string) => void;
  updateAdditionalTask: (id: string, value: Partial<ProductionWorkflowTaskT>) => void;
  updateTemporaryAdditionalTask: (id: string, value: string) => void;
  handleUser: (taskId: string, user: UserShortModel, slot_id: string, type: 'assign' | 'unassign') => void;
};
type Props = DispatchProps & OwnProps & StateProps;

const AdditionalTaskItem: FC<Props> = ({
  taskInfo,
  highlightName,
  productionStatus,
  isProcessingGuard,
  isShowDashedLine = false,
  isItemForQualityControl = false,
  handleUser,
  createAdditionalTask,
  updateAdditionalTask,
  copyAdditionalTaskLink,
  openDeleteConfirmationModal,
  deleteTemporaryAdditionalTask,
  updateTemporaryAdditionalTask,
  closeManageFailedTasksModal,
}) => {
  const navigate = useNavigate();

  const isTemporaryTask = useMemo(() => taskInfo?.id?.split('-')?.[0] === 'new', [taskInfo?.id]);

  const handleOnEditEnd = (value: string) => {
    if (isItemForQualityControl && isTemporaryTask) return updateTemporaryAdditionalTask(taskInfo.id, value);

    if (isTemporaryTask) return createAdditionalTask(taskInfo.id, value);

    return updateAdditionalTask(taskInfo.id, { name: value });
  };

  const handleOnEditModeEnd = (value: string) => {
    if (!value && isItemForQualityControl && isTemporaryTask) return deleteTemporaryAdditionalTask(taskInfo.id);

    if (isItemForQualityControl && isTemporaryTask) return '';

    if (!value || isTemporaryTask) return deleteTemporaryAdditionalTask(taskInfo.id);

    return '';
  };

  const accessLevels = [AccessLevel.userDepartment, AccessLevel.userDepartmentSubDepartments, AccessLevel.allDepartments];
  const departmentsAll = taskInfo.departmets_all;
  const isEditPermitted = useSelector((state: AppState) =>
    PermissionGuardSelectors.checkEditLevel(state, Permission.webTaskEditLevel, accessLevels, departmentsAll),
  );

  const getSlots = (info: ProductionWorkflowTaskTWithIssues): MultipleAssigneeSelectorSlotsT[] => {
    return info?.taskResponsibilities?.flatMap((responsibility) =>
      responsibility.taskSlots.map((slot) => ({
        id: slot.id,
        responsibilityId: slot.task_responsibility_id,
        assignedUserId: slot.taskAssignment?.user?.id,
        last_name: slot.taskAssignment?.user?.last_name,
        first_name: slot.taskAssignment?.user?.first_name,
        avatar_image_url: slot.taskAssignment?.user?.avatar_image_url,
        department: slot.taskAssignment?.user?.user_position_slots?.[0]?.position_slot.department,
        position: slot.taskAssignment?.user?.user_position_slots?.[0]?.position_slot.position_type,
      })),
    );
  };

  const handleNavigateToItem = () => {
    navigate(`${Paths.Task}/${taskInfo.id}`);
    closeManageFailedTasksModal();
  };

  return (
    <div
      data-is-item-for-quality-control={isItemForQualityControl}
      data-is-temporary-task={isTemporaryTask}
      data-is-show-dashed-line={isShowDashedLine}
      className={taskInfo.issues?.length ? `${s.container} ${s.container_with_warning}` : s.container}
      onClick={() => {
        if (!isTemporaryTask) {
          handleNavigateToItem();
        }
      }}
    >
      {!isTemporaryTask && !isItemForQualityControl && <DragIndicatorIcon className={`${s.drag_area} handle-area`} />}
      {!!taskInfo.issues?.length && !isItemForQualityControl && (
        <LightTooltip className={s.tooltip} placement="top-start" title={<IssuesList issues={taskInfo.issues} />}>
          <div className={s.warning_wrapper}>
            <Tag
              color="orange"
              className={s.badge}
              iconLeft={
                <Stack direction="row" alignItems="center" gap="3px">
                  <WarningIcon width={20} height={20} />
                </Stack>
              }
            />
          </div>
        </LightTooltip>
      )}

      <TextEditable
        inputMinWidth={250}
        allowedEmpty={false}
        textClassName={s.title}
        style={{ width: '250px' }}
        focused={isTemporaryTask}
        value={taskInfo.name || ''}
        highlitedValue={highlightName}
        placeholderClassName={s.placeholder_name}
        textValidationTitle={<MaxLettersWarning />}
        onEditEnd={handleOnEditEnd}
        isNotClickable={!isEditPermitted}
        onClick={(e) => e.stopPropagation()}
        onEditModeEnd={handleOnEditModeEnd}
      />
      {!isTemporaryTask && (
        <>
          {isProcessingGuard ? (
            <ProcessingIndicator isProcessingGuard={isProcessingGuard} enableStopPropagation className={s.process_container} />
          ) : (
            <StatusSelector
              optionsFor="task"
              enableStopPropagation
              disableDropDown={isProcessingGuard}
              status={taskInfo.status}
              isEditPermitted={isEditPermitted}
              isProcessingGuard={isProcessingGuard}
              productionStatus={productionStatus}
              containerClassName={s.status_selector}
              onSelect={async (status) => {
                await updateAdditionalTask(taskInfo.id, { status });
              }}
            />
          )}

          <MultipleAssigneeSelector
            isAvatarOnly
            enableStopPropagation
            isEditPermitted={isEditPermitted}
            isProcessingGuard={isProcessingGuard}
            slots={taskInfo ? getSlots(taskInfo) : []}
            unassignUser={(user, slotId) => handleUser(taskInfo.id, user, slotId, 'unassign')}
            assignUser={(user, slotId) => handleUser(taskInfo.id, user, slotId, 'assign')}
          />
          <PrioritySelector
            priority={taskInfo.priority}
            isProcessingGuard={isProcessingGuard}
            isEditPermitted={isEditPermitted}
            onSelect={(value: string) => updateAdditionalTask(taskInfo.id, { priority: value })}
          />
          <ActionsMenu
            itemId={taskInfo.id}
            taskDepartments={departmentsAll}
            buttonClassName={s.action_button}
            isProcessingGuard={isProcessingGuard}
            isRootAdditionalTask={taskInfo?.reopenedTasks && taskInfo.reopenedTasks?.length > 0}
            isTaskFromRootHierarchy={taskInfo.failed_task_id}
            copyAdditionalTaskLink={copyAdditionalTaskLink}
            openDeleteConfirmationModal={openDeleteConfirmationModal}
          />
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: AppState): StateProps => ({
  productionStatus: state.production_workflow.productionInfo.status,
});
const mapDispatchToProps: DispatchProps = {
  handleUser: ProductionWorkflowAdditionalTasksActions.handleUser,
  createAdditionalTask: ProductionWorkflowAdditionalTasksActions.createAdditionalTask,
  updateAdditionalTask: ProductionWorkflowAdditionalTasksActions.updateAdditionalTask,
  copyAdditionalTaskLink: ProductionWorkflowAdditionalTasksActions.copyAdditionalTaskLink,
  openDeleteConfirmationModal: ProductionWorkflowAdditionalTasksActions.openDeleteConfirmationModal,
  deleteTemporaryAdditionalTask: ProductionWorkflowAdditionalTasksActions.deleteTemporaryAdditionalTask,
  updateTemporaryAdditionalTask: ProductionWorkflowAdditionalTasksActions.updateTemporaryAdditionalTask,
  closeManageFailedTasksModal: ManageFailedTasksActions.closeManageFailedTasksModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdditionalTaskItem);
